import { createFileRoute, Link, notFound } from '@tanstack/react-router'
import { states, type StateStatus } from '@/lib/seo/states'

export const Route = createFileRoute('/online-poker/$state/$city')({
  loader: ({ params }) => {
    const state = states.find((s) => s.slug === params.state)
    if (!state) throw notFound()
    const city = state.cities.find((c) => c.slug === params.city)
    if (!city) throw notFound()
    return { state, city }
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {}
    const { state, city } = loaderData
    return {
      meta: [
        {
          title: `Online Poker in ${city.name}, ${state.name} | Powka`,
        },
        {
          name: 'description',
          content: `Online poker laws in ${city.name}, ${state.name}. Learn about the legal status and what players in ${city.name} need to know.`,
        },
        {
          property: 'og:title',
          content: `Online Poker in ${city.name}, ${state.name} | Powka`,
        },
        { property: 'og:type', content: 'article' },
      ],
    }
  },
  component: RouteComponent,
})

const statusConfig: Record<
  StateStatus,
  { label: string; className: string; description: string }
> = {
  legal: {
    label: 'Legal & Regulated',
    className:
      'bg-green-500/10 text-green-600 dark:text-green-400 border border-green-500/20',
    description:
      'Online poker is legal and regulated in this state. Licensed operators are available to residents.',
  },
  'gray-area': {
    label: 'Unregulated',
    className:
      'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border border-yellow-500/20',
    description:
      'Online poker is not regulated in this state. No state-licensed operators exist, though offshore platforms are accessible.',
  },
  illegal: {
    label: 'Prohibited',
    className:
      'bg-red-500/10 text-red-600 dark:text-red-400 border border-red-500/20',
    description:
      'Online poker is prohibited in this state. Players should be aware of local gambling laws before playing.',
  },
}

function RouteComponent() {
  const { state, city } = Route.useLoaderData()
  const status = statusConfig[state.status]

  return (
    <div className="max-w-4xl mx-auto px-6 py-16">
      <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-8">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span>/</span>
        <Link to="/online-poker" className="hover:text-foreground">
          Online Poker
        </Link>
        <span>/</span>
        <Link
          to="/online-poker/$state"
          params={{ state: state.slug }}
          className="hover:text-foreground"
        >
          {state.name}
        </Link>
        <span>/</span>
        <span className="text-foreground">{city.name}</span>
      </nav>

      <div className="mb-4">
        <h1 className="text-3xl font-bold">
          Online Poker in {city.name}, {state.name}
        </h1>
      </div>

      <span
        className={`inline-block text-xs font-semibold px-3 py-1 rounded-full mb-8 ${status.className}`}
      >
        {status.label}
      </span>

      <p className="text-muted-foreground leading-relaxed mb-10">
        Players in {city.name} are subject to {state.name} state gambling laws.{' '}
        {status.description} As with all residents of {state.name}, those in{' '}
        {city.name} should review applicable state regulations before
        participating in real-money online poker.
      </p>

      <div className="mb-12">
        <h2 className="text-lg font-semibold mb-4">Key Facts</h2>
        <ul className="space-y-3">
          {[
            `Online poker in ${city.name} falls under ${state.name} state law — ${status.label.toLowerCase()}`,
            `There are no city-specific online poker regulations in ${city.name}; state law governs all online gambling activity`,
            `Players physically located in ${city.name} are bound by ${state.name}'s online poker rules, regardless of where a platform is licensed`,
            `Geolocation technology used by licensed operators confirms a player's physical location at the time of play`,
            `USDC-based platforms like Powka offer a transparent, low-rake alternative to traditional online poker sites`,
          ].map((fact, i) => (
            <li key={i} className="flex gap-3">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" />
              <span className="text-muted-foreground">{fact}</span>
            </li>
          ))}
        </ul>
      </div>

      <div className="rounded-lg border bg-secondary/30 px-6 py-5">
        <h2 className="font-semibold mb-1">Play on Powka</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Powka is a no-limit Texas Hold'em platform with a 1% rake. Deposits
          and withdrawals are handled in USDC.
        </p>
        <Link
          to="/"
          className="inline-block rounded-md bg-foreground text-background text-sm font-medium px-4 py-2 hover:opacity-90 transition-opacity"
        >
          Start playing
        </Link>
      </div>

      <div className="mt-10 flex flex-col gap-2">
        <Link
          to="/online-poker/$state"
          params={{ state: state.slug }}
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Back to {state.name}
        </Link>
        <Link
          to="/online-poker"
          className="text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          ← View all states
        </Link>
      </div>
    </div>
  )
}
